// Typed client for the /accounts/* REST API (LT-133).

import axios from 'axios';
import {
  Account,
  AccountCreate,
  AccountUpdate,
  Activity,
  Contact,
} from '../types/contact';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export const accountsAPI = {
  list: async ({
    q,
    limit = 100,
    offset = 0,
  }: { q?: string; limit?: number; offset?: number } = {}): Promise<Account[]> => {
    const params: Record<string, string | number> = { limit, offset };
    if (q) params.q = q;
    const r = await client.get<Account[]>('/accounts/', { params });
    return r.data;
  },

  get: async (id: string): Promise<Account> => {
    const r = await client.get<Account>(`/accounts/${id}`);
    return r.data;
  },

  create: async (payload: AccountCreate): Promise<Account> => {
    const r = await client.post<Account>('/accounts/', payload);
    return r.data;
  },

  update: async (id: string, patch: AccountUpdate): Promise<Account> => {
    const r = await client.patch<Account>(`/accounts/${id}`, patch);
    return r.data;
  },

  softDelete: async (id: string): Promise<void> => {
    await client.delete(`/accounts/${id}`);
  },

  listContacts: async (
    accountId: string,
    { limit = 100, offset = 0 }: { limit?: number; offset?: number } = {}
  ): Promise<Contact[]> => {
    const r = await client.get<Contact[]>(`/accounts/${accountId}/contacts`, {
      params: { limit, offset },
    });
    return r.data;
  },

  listActivities: async (
    accountId: string,
    { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}
  ): Promise<Activity[]> => {
    const r = await client.get<Activity[]>(`/accounts/${accountId}/activities`, {
      params: { limit, offset },
    });
    return r.data;
  },
};
